import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { Activity, Filter, TrendingUp, TrendingDown } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, PieChart, Pie, Cell, Legend } from "recharts";

interface DecisionRow {
  model_used: string;
  task_class: string;
  provider_used: string;
  reason: string | null;
  was_fallback: boolean | null;
  success: boolean | null;
  latency_ms: number | null;
  estimated_cost_usd: number | null;
  created_at: string | null;
}

const REASON_COLORS: Record<string, string> = {
  override: "hsl(var(--destructive))",
  preferred: "hsl(var(--primary))",
  agent_override: "hsl(var(--primary))",
  default: "hsl(var(--muted-foreground))",
};

function reasonBucket(reason: string | null): string {
  if (!reason) return "default";
  if (reason.startsWith("adaptive")) return "adaptive";
  if (reason === "override") return "override";
  if (reason === "preferred" || reason === "agent_override") return "preferred";
  if (reason.startsWith("skill_chain")) return "chain";
  return reason;
}

export default function ModelDecisions() {
  const { data: orgId } = useCurrentOrgId();
  const [windowDays, setWindowDays] = useState<number>(7);
  const [taskFilter, setTaskFilter] = useState<string>("all");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["model-decisions", orgId ?? "no-org", windowDays],
    enabled: !!orgId,
    queryFn: async (): Promise<DecisionRow[]> => {
      const since = new Date(Date.now() - windowDays * 24 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("model_decisions")
        .select("model_used, task_class, provider_used, reason, was_fallback, success, latency_ms, estimated_cost_usd, created_at")
        .eq("org_id", orgId!)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(5000);
      if (error) throw error;
      return (data ?? []) as DecisionRow[];
    },
  });

  const filtered = useMemo(
    () => (taskFilter === "all" ? rows : rows.filter((r) => r.task_class === taskFilter)),
    [rows, taskFilter],
  );

  const taskClasses = useMemo(() => Array.from(new Set(rows.map((r) => r.task_class))).sort(), [rows]);

  const stats = useMemo(() => {
    const total = filtered.length;
    const successes = filtered.filter((r) => r.success).length;
    const fallbacks = filtered.filter((r) => r.was_fallback).length;
    const totalCost = filtered.reduce((s, r) => s + (r.estimated_cost_usd ?? 0), 0);
    const avgLatency = total
      ? Math.round(filtered.reduce((s, r) => s + (r.latency_ms ?? 0), 0) / total)
      : 0;
    return {
      total,
      successRate: total ? Math.round((successes / total) * 100) : 0,
      fallbackRate: total ? Math.round((fallbacks / total) * 100) : 0,
      totalCost,
      avgLatency,
    };
  }, [filtered]);

  const byModel = useMemo(() => {
    const m = new Map<string, { count: number; latency: number; cost: number; ok: number }>();
    for (const r of filtered) {
      const cur = m.get(r.model_used) ?? { count: 0, latency: 0, cost: 0, ok: 0 };
      cur.count++;
      cur.latency += r.latency_ms ?? 0;
      cur.cost += r.estimated_cost_usd ?? 0;
      if (r.success) cur.ok++;
      m.set(r.model_used, cur);
    }
    return Array.from(m.entries())
      .map(([model, s]) => ({
        model: model.split("/").pop() || model,
        full: model,
        count: s.count,
        avgMs: Math.round(s.latency / s.count),
        successRate: Math.round((s.ok / s.count) * 100),
        cost: Number(s.cost.toFixed(5)),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filtered]);

  const byReason = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of filtered) {
      const bucket = reasonBucket(r.reason);
      m.set(bucket, (m.get(bucket) ?? 0) + 1);
    }
    return Array.from(m.entries()).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Skill OS" }, { label: "Decisões IA" }]} />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-headline flex items-center gap-2">
            <Activity className="w-6 h-6" /> Decisões de Modelo
          </h1>
          <p className="text-muted-foreground text-sm">
            Inspeção das escolhas do gateway adaptativo — por que cada modelo foi escolhido, taxa de fallback, custo e latência.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Últ. 24h</SelectItem>
              <SelectItem value="7">Últ. 7 dias</SelectItem>
              <SelectItem value="30">Últ. 30 dias</SelectItem>
            </SelectContent>
          </Select>
          <Select value={taskFilter} onValueChange={setTaskFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas task classes</SelectItem>
              {taskClasses.map((tc) => <SelectItem key={tc} value={tc}>{tc}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : !filtered.length ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground text-sm">
            Nenhuma decisão registrada no período.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Total decisões" value={stats.total.toLocaleString()} />
            <KPI label="Taxa de sucesso" value={`${stats.successRate}%`} positive={stats.successRate >= 95} />
            <KPI label="Taxa fallback" value={`${stats.fallbackRate}%`} positive={stats.fallbackRate < 10} invert />
            <KPI label="Custo total" value={`$${stats.totalCost.toFixed(4)}`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Top modelos por volume</CardTitle>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byModel} layout="vertical" margin={{ left: 8, right: 8 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="model" width={140} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                      formatter={(v: number, _n, item: any) => [`${v} chamadas`, item.payload.full]}
                    />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Razão da escolha</CardTitle>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={byReason} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                      {byReason.map((entry, i) => (
                        <Cell key={entry.name} fill={REASON_COLORS[entry.name] || `hsl(${(i * 67) % 360} 70% 55%)`} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Performance por modelo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {byModel.map((m) => (
                  <div key={m.full} className="flex items-center gap-3 text-sm py-2 border-b border-border/50 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs truncate">{m.full}</p>
                      <p className="text-xs text-muted-foreground">{m.count} chamadas · {m.avgMs}ms média · ${m.cost.toFixed(5)}</p>
                    </div>
                    <Badge variant={m.successRate >= 95 ? "default" : m.successRate >= 80 ? "secondary" : "destructive"} className="text-xs">
                      {m.successRate}%
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, positive, invert }: { label: string; value: string; positive?: boolean; invert?: boolean }) {
  const showIcon = positive !== undefined;
  const good = invert ? !positive : positive;
  return (
    <Card>
      <CardContent className="py-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-foreground mt-1 flex items-center gap-1.5">
          {value}
          {showIcon && (good
            ? <TrendingUp className="w-4 h-4 text-primary" />
            : <TrendingDown className="w-4 h-4 text-destructive" />)}
        </p>
      </CardContent>
    </Card>
  );
}
